import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

function ProductModal({ show, onHide, product, onUpdated }) {
  const [form, setForm] = useState({ ...product });
  const [units, setUnits] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const categoryOptions = [
    { value: 'Row Material', label: 'Row Material' },
    { value: 'Finished Good', label: 'Finished Good' },
        { value: 'Scrap', label: 'Scrap' }
  ];

  const gstOptions = [
    { value: 0, label: '0' },
    { value: 5, label: '5%' },
    { value: 8, label: '8%' },
    { value: 12, label: '12%' },
    { value: 18, label: '18%' }
  ];
  const cessOptions = [
    { value: 0, label: '0' },
    { value: 2, label: '2%' },
  ];

  useEffect(() => {
    setForm({ ...product });

    if (show) {
      axios
        .get(`${config.API_BASE_URL}/units`)
        .then((res) => setUnits(res.data))
        .catch((err) => console.error('Failed to fetch units', err));
      
      axios
        .get(`${config.API_BASE_URL}/groups`)
        .then((res) => setGroups(res.data))
        .catch((err) => console.error('Failed to fetch groups', err));
    }
  }, [show, product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Remove gst and cess from required fields check
    if (!form.unitId || !form.category || !form.alias || !form.sku) {
      toast.error('Please fill all required fields.');
      return;
    }

    try {
      await axios.put(`${config.API_BASE_URL}/products/${product._id}`, {
        ...form,
        gst: form.gst || 0,  // Default to 0 if not selected
        cess: form.cess || 0  // Default to 0 if not selected
      });
      onUpdated();
      onHide();
      toast.success('Product updated successfully!');
    } catch (err) {
      console.error('Update failed', err);
      toast.error('Failed to update product!');
    }
  };

  const handleCreateGroup = async (inputValue) => {
    setIsCreatingGroup(true);
    try {
      const newGroup = {
        groupName: inputValue,
        groupCode: inputValue.substring(0, 3).toUpperCase(),
      };

      const response = await axios.post(`${config.API_BASE_URL}/groups/new`, newGroup);
      const createdGroup = response.data.group;
      
      // Update groups list
      setGroups(prev => [...prev, createdGroup]);
      
      // Select the newly created group
      setForm(prev => ({ ...prev, groupId: createdGroup._id }));
      
      toast.success('New group created successfully');
      return createdGroup;
    } catch (error) {
      toast.error('Failed to create new group');
      console.error('Error creating group:', error);
      return null;
    } finally {
      setIsCreatingGroup(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      fullscreen="lg-down"
      backdrop={isCreatingGroup ? 'static' : true}
      keyboard={!isCreatingGroup}
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find(opt => opt.value === form.category)}
                  onChange={(selected) => setForm((prev) => ({ ...prev, category: selected?.value || '' }))}
                  placeholder="Select category"
                  isClearable
                />
                {!form.category && <div className="text-danger mt-1">Required</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Product Name</Form.Label>
                <Form.Control
                  name="productName"
                  value={form.productName || ''}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Alias</Form.Label>
                <Form.Control
                  name="alias"
                  value={form.alias || ''}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>HSN Code</Form.Label>
                <Form.Control
                  name="productCode"
                  value={form.productCode || ''}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>


            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>SKU</Form.Label>
                <Form.Control
                  name="sku"
                  value={form.sku || ''}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>GST (%)</Form.Label>
                <Select
                  options={gstOptions}
                  value={gstOptions.find(opt => opt.value === Number(form.gst))}
                  onChange={(selected) => setForm((prev) => ({ ...prev, gst: selected?.value || 0 }))}
                  placeholder="Select GST"
                  isClearable
                />
                {/* {!form.gst && <div className="text-danger mt-1">Required</div>} */}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>CESS (%)</Form.Label>
                <Select
                  options={cessOptions}
                  value={cessOptions.find(opt => opt.value === Number(form.cess))}
                  onChange={(selected) => setForm((prev) => ({ ...prev, cess: selected?.value || 0 }))}
                  placeholder="Select CESS"
                  isClearable
                />
                {/* {!form.cess && <div className="text-danger mt-1">Required</div>} */}
              </Form.Group>
            </Col>


            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Unit</Form.Label>
                <Select
                  options={units.map((unit) => ({
                    value: unit._id,
                    label: `${unit.unitName} (${unit.unitCode})`,
                  }))}
                  value={
                    (() => {
                      // Handle both string and populated object
                      const unitId = form.unitId?._id || form.unitId;
                      const unitObj = units.find((u) => u._id === unitId);
                      return unitObj
                        ? {
                            value: unitObj._id,
                            label: `${unitObj.unitName} (${unitObj.unitCode})`,
                          }
                        : null;
                    })()
                  }
                  onChange={(selected) =>
                    setForm((prev) => ({
                      ...prev,
                      unitId: selected?.value || '',
                    }))
                  }
                  placeholder="Select unit"
                  isClearable
                />
                {!form.unitId && <div className="text-danger mt-1">Required</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Group</Form.Label>
                <CreatableSelect
                  isClearable
                  isDisabled={isCreatingGroup}
                  isLoading={isCreatingGroup}
                  options={groups.map((group) => ({
                    value: group._id,
                    label: `${group.groupName} (${group.groupCode})`
                  }))}
                  value={
                    (() => {
                      const groupId = form.groupId?._id || form.groupId;
                      const groupObj = groups.find(g => g._id === groupId);
                      return groupObj
                        ? {
                            value: groupObj._id,
                            label: `${groupObj.groupName} (${groupObj.groupCode})`
                          }
                        : null;
                    })()
                  }
                  onChange={(selected) => setForm(prev => ({ 
                    ...prev, 
                    groupId: selected?.value || '' 
                  }))}
                  onCreateOption={handleCreateGroup}
                  placeholder="Select or create group..."
                  formatCreateLabel={(inputValue) => `Create group "${inputValue}"`}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Buffer Stock</Form.Label>
                <Form.Control
                  type="number"
                  name="bufferStock"
                  value={form.bufferStock || 0}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>
          </Row>



          <Form.Check
            type="switch"
            className='d-none'
            label={form.isActive ? 'Active' : 'Inactive'}
            checked={form.isActive || false}
            onChange={() =>
              setForm((prev) => ({ ...prev, isActive: !prev.isActive }))
            }
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Update
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ProductModal;
